import Link from "next/link";

import { FUOCO_HOME } from "@/components/home/stile";
import { Icona, type NomeIcona } from "@/components/home/Icona";
import { LogoOI } from "@/components/common/LogoOI";
import { DisegnoServizio } from "@/components/servizi/catalogo/DisegnoServizio";
import type { ServizioPagina } from "@/lib/serviziPagina";

/* Le caselle della griglia di /servizi (§5.3-5.5 delle istruzioni): la
   card del servizio, la tessera dell'area e la tessera «Non trovi la tua
   esigenza?». Misure del modello `uploads/servizi 01 oi.html`, tradotte
   in classi.

   Dal 17/10/2026 /servizi è la versione «Parti dalla domanda» e non ha più
   né card né tessere: le due tessere (`TesseraArea`, `TesseraEsigenza`)
   sono state tolte, la card resta per «Esplora gli altri servizi» delle
   pagine dei singoli servizi (`AltriServizi`) e `RiquadroIcona` per le
   loro voci. L'opzione `grande` di `RiquadroIcona` era della tessera
   d'area: oggi nessuno la passa. */

/** Il riquadro dell'icona: angolo in alto a destra tagliato a 45° (10
 *  pixel) e un triangolino rame di 12 nell'angolo, come il marchio OI.
 *  40 × 40 con l'icona a 24 nella card, 56 × 56 con l'icona a 32 nella
 *  tessera, a colori invertiti. Il modello li scrive in `content-box`
 *  (40 più un pixel di bordo per lato), Tailwind è `border-box`: da qui
 *  42 e 58, che sono le misure rese dalla tavola. */
export function RiquadroIcona({
  nome,
  grande = false,
  piccolo = false,
  minimo = false,
}: {
  nome: NomeIcona;
  grande?: boolean;
  /** 34 × 34 con l'icona a 20: le voci del perimetro di
   *  /servizi/monitoraggio (`.m-per .bdg` del suo modello). */
  piccolo?: boolean;
  /** 30 × 30 più il bordo (32), icona a 18: la testa di ogni fase di
   *  /servizi/analisi (`.ph-hd .bdg` del suo modello). */
  minimo?: boolean;
}) {
  return (
    <span
      aria-hidden
      className={`relative grid shrink-0 place-items-center border [clip-path:polygon(0_0,calc(100%-10px)_0,100%_10px,100%_100%,0_100%)] ${
        grande
          ? "size-[58px] border-[var(--oi-sv-su-tessera)] bg-[var(--oi-sv-su-tessera)] text-[var(--oi-sv-tessera)]"
          : `${minimo ? "size-[32px]" : piccolo ? "size-[36px]" : "size-[42px]"} border-mare bg-bg text-mare`
      }`}
    >
      <Icona
        nome={nome}
        className={grande ? "size-8" : minimo ? "size-[18px]" : piccolo ? "size-5" : "size-6"}
      />
      <span className="absolute top-0 right-0 size-3 bg-segnale [clip-path:polygon(0_0,100%_0,100%_100%)]" />
    </span>
  );
}

/** Frase, titolo e descrizione della card: la parte che cambia da un
 *  servizio all'altro. */
function TestoCard({ s }: { s: ServizioPagina }) {
  return (
    <>
      <span className="flex items-center gap-3">
        <RiquadroIcona nome={s.icona} />
        <span className="font-sans text-voce font-semibold text-segnale">{s.frase}</span>
      </span>
      <span className="font-serif text-[26px] leading-[1.2] transition-colors group-hover:text-mare">
        {s.titolo}
      </span>
      <span className="font-sans text-sommario leading-[1.5] text-sommesso">{s.descrizione}</span>
    </>
  );
}

/** La card di un servizio: tutta la card è il collegamento.
 *
 *  `riserva` (19/10/2026) serve alle card che cambiano servizio dopo
 *  l'idratazione (`AltriServiziARotazione`): i testi di quei servizi
 *  stanno, invisibili e fuori dall'albero accessibile, nella stessa cella
 *  di griglia del testo vero, così la card è alta quanto il servizio più
 *  lungo fra quelli che possono uscire e il cambio non sposta niente
 *  (niente CLS). Senza `riserva` la card è quella di sempre. */
export function CardServizio({
  s,
  totale,
  riserva,
}: {
  s: ServizioPagina;
  totale: number;
  riserva?: ServizioPagina[];
}) {
  return (
    <Link
      href={`/servizi/${s.slug}`}
      className={`group flex flex-col overflow-hidden border border-t-4 border-ink border-t-segnale bg-surface text-ink no-underline ${FUOCO_HOME}`}
    >
      <DisegnoServizio slug={s.slug} />
      <span className="flex flex-1 flex-col gap-2.5 px-[22px] pt-[18px] pb-4">
        {riserva?.length ? (
          <span className="grid">
            <span className="col-start-1 row-start-1 flex flex-col gap-2.5">
              <TestoCard s={s} />
            </span>
            {riserva.map((r) => (
              <span key={r.slug} aria-hidden className="invisible col-start-1 row-start-1 flex flex-col gap-2.5">
                <TestoCard s={r} />
              </span>
            ))}
          </span>
        ) : (
          <TestoCard s={s} />
        )}
        <span className="mt-auto flex items-center justify-between border-t border-rule pt-2.5">
          <span className="inline-flex items-center gap-[7px] font-mono text-[11px] tracking-[0.06em] text-sommesso uppercase">
            <LogoOI
              variant="segno"
              decorative
              accento="fill-segnale"
              className="h-[13px] w-4 shrink-0 text-mare"
            />
            Servizio {s.numero} di {totale}
          </span>
          <span
            aria-hidden
            className="font-semibold text-mare transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
          >
            ↗
          </span>
        </span>
      </span>
    </Link>
  );
}
